import { useAuth } from '@/contexts/AuthContext';
import { Navigate, Route, Routes, useParams, useSearchParams } from 'react-router-dom';

// Layouts
import { AdminLayout, UserLayout } from '@/components/layout';

// Auth Components
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { RoleGuard } from '@/components/auth/RoleGuard';

// Auth Pages
import CompleteRegistrationPage from '@/pages/CompleteRegistrationPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import LoginPage from '@/pages/LoginPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import SignupPage from '@/pages/SignupPage';
import VerifyEmailPage from '@/pages/VerifyEmailPage';

// User Pages
import DashboardPage from '@/pages/DashboardPage';
import ProfilePage from '@/pages/ProfilePage';
import { DocumentsPage, TaxFilingsPage, TaxFormLandingPage, TaxFormEditorPage, UploadPage } from '@/pages/user';

// Admin Pages
import { WhatsAppChatWidget } from '@/components/common';
import {
  AdminDashboardPage,
  AdminSettingsPage,
  UserDetailPage,
  UsersPage,
  AdminTaxFormLandingPage,
  AdminTaxFormEditorPage,
} from '@/pages/admin';
import { TAX_FILINGS_TAB } from '@/types/taxFilings';

const VALID_FILING_TABS = new Set<string>(Object.values(TAX_FILINGS_TAB));

function AdminUserDetailRedirect() {
  const { userId } = useParams<{ userId: string }>();
  return (
    <Navigate
      to={`/admin/users/${encodeURIComponent(userId ?? '')}/documents`}
      replace
    />
  );
}

function AdminUserTaxFilingsRedirect() {
  const { userId } = useParams<{ userId: string }>();
  return (
    <Navigate
      to={`/admin/users/${encodeURIComponent(userId ?? '')}/tax-filings/tax-summary`}
      replace
    />
  );
}

function TaxFilingsIndexRedirect() {
  const [searchParams] = useSearchParams();
  const legacyTab = searchParams.get('tab');
  if (legacyTab && VALID_FILING_TABS.has(legacyTab)) {
    return <Navigate to={`/tax-filings/${legacyTab}`} replace />;
  }
  return <Navigate to="/tax-filings/tax-summary" replace />;
}

function RootRedirect() {
  const { isAuthenticated, isAdmin, isLoading } = useAuth();

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to={isAdmin() ? '/admin' : '/dashboard'} replace />;
}

function App() {
  const { isAdmin } = useAuth();

  return (
    <>
      <Routes>
        {/* Root redirect based on auth status and role */}
        <Route path="/" element={<RootRedirect />} />

        {/* Public Auth Routes */}
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/email-verification" element={<VerifyEmailPage />} />
        <Route path="/complete-registration" element={<CompleteRegistrationPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Protected User Routes */}
        <Route
          element={
            <ProtectedRoute>
              <RoleGuard allowedRoles={['role_user']} fallbackPath="/admin">
                <UserLayout />
              </RoleGuard>
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/documents" element={<DocumentsPage />} />
          <Route path="/tax-filings" element={<TaxFilingsIndexRedirect />} />
          <Route path="/tax-filings/:tab" element={<TaxFilingsPage />} />
          <Route path="/filings" element={<Navigate to="/tax-filings/tax-summary" replace />} />
          <Route path="/tax-form" element={<TaxFormLandingPage />} />
          <Route path="/tax-form/:formId" element={<TaxFormEditorPage />} />
          <Route path="/upload" element={<UploadPage />} />
          <Route path="/profile" element={<ProfilePage />} />
        </Route>

        {/* Protected Admin Routes */}
        <Route
          element={
            <ProtectedRoute>
              <RoleGuard allowedRoles={['role_admin', 'role_superadmin']} fallbackPath="/dashboard">
                <AdminLayout />
              </RoleGuard>
            </ProtectedRoute>
          }
        >
          <Route path="/admin" element={<AdminDashboardPage />} />
          <Route path="/admin/users" element={<UsersPage />} />
          <Route path="/admin/users/:userId" element={<AdminUserDetailRedirect />} />
          <Route path="/admin/users/:userId/documents" element={<UserDetailPage />} />
          <Route path="/admin/users/:userId/tax-filings" element={<AdminUserTaxFilingsRedirect />} />
          <Route path="/admin/users/:userId/tax-filings/:filingTab" element={<UserDetailPage />} />
          <Route path="/admin/users/:userId/tax-form" element={<AdminTaxFormLandingPage />} />
          <Route path="/admin/users/:userId/tax-form/:formId" element={<AdminTaxFormEditorPage />} />
          <Route path="/admin/profile" element={<ProfilePage />} />
          <Route path="/admin/settings" element={<AdminSettingsPage />} />
        </Route>

        {/* Catch all - redirect to root */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {!isAdmin() && <WhatsAppChatWidget />}
    </>
  );
}

export default App;
